<template>
  <div class="dropdown">
    <button
      v-if="hasOnlyOneAction"
      @click="tryPerformAction(orderedActions[0], listView)"
      :disabled="isPerformingAction"
      type="button"
      class="btn action-btn green"
    >
      <template v-if="generatingDocument">
        <xpinner :has-background="false" color="White" speed="fast"/>
      </template>
      {{orderedActions[0].actionName}}
    </button>
    <template v-else-if="orderedActions.length > 0">
      <button
        class="dropdown-toggle arrow btn action-btn green"
        :disabled="isPerformingAction"
        :id="`actionsDropdown-${dropdownControlId}`"
        data-toggle="dropdown"
        aria-haspopup="true"
        aria-expanded="false"
      >
        <template v-if="generatingDocument">
          <xpinner :has-background="false" color="White" speed="fast"/>
        </template>
        Actions
      </button>

      <ul class="dropdown-menu dropdown-menu-right uncover" :aria-labelledby="`actionsDropdown-${dropdownControlId}`">
        <li v-for="action in orderedActions" :key="action.action_client_id">
          <button
            type="button"
            class="dropdown-item"
            @click="tryPerformingAction(action, listView)"
          >{{action.actionName}}</button>
        </li>
      </ul>
    </template>

    <action-confirmation-modal
      v-if="modalStateFor(vueModalID.confirmObjectModelAction).isVisible"
      :state="modalStateFor(vueModalID.confirmObjectModelAction)"
      :index="vueModalID.confirmObjectModelAction"
      @toggle-modal="handleCloseConfirmationModal"
      :overrideConfig="actionConfirmationModalConfig"
      :context="NotesTabContext.Records"
      :entry="entry"
      :note-required="selectedObjectModelAction.require_note"
      :action-title="selectedObjectModelAction.label_title"
    ></action-confirmation-modal>

    <multiple-submissions-modal
      v-if="modalStateFor(vueModalID.multipleSubmissions).isVisible"
      :state="modalStateFor(vueModalID.multipleSubmissions)"
      :index="vueModalID.multipleSubmissions"
      @start-form="startForm"
      @edit-form="editForm"
      @toggle-modal="hideModal(vueModalID.multipleSubmissions)">
      ></multiple-submissions-modal>

    <object-model-action-error-modal
      :state="modalStateFor(vueModalID.objectModelActionError)"
      :index="vueModalID.objectModelActionError"
      @toggle-modal="handleCloseErrorModal"
      :error-title="actionErrorTitle"
      :error-message="actionErrorMessage"
    ></object-model-action-error-modal>

    <object-model-action-custom-notification
      v-if="!isPreview"
      :state="modalStateFor(vueModalID.objectModelActionCustomNotification)"
      :index="vueModalID.objectModelActionCustomNotification"
      :entry="associatedObjectModelEntry"
      @toggle-modal="handleCloseCustomNotificationModal"
      @send="sendCustomNotification(associatedObjectModelEntry, $event)"
    ></object-model-action-custom-notification>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import ObjectModelFakeEntry from "~/models/objectModelEntries/objectModelFakeEntry";
import ObjectModelEntry from "~/models/objectModelEntries/objectModelEntry";
import ListViewContentElement from "~/models/listViews/elements/listViewContentElement";
import ListView from "~/models/listViews/listView";
import PublishedListView from "~/models/listViews/publishedListView";
import objectModelActionConfirmationMixin from "../../../modelViews/mixins/objectModelActionConfirmationMixin";
import objectModelActionPerformMixin from "../../../modelViews/mixins/objectModelActionPerformMixin";
import ObjectModelActionErrorModal from "../../../modelViews/components/object-model-action-error-modal.vue";
import ObjectModelActionCustomNotification from "../../../modelViews/components/object-model-action-custom-notification.vue";
import {NotesTabContext} from '../../../formSubmission/enums/notesTabContext';
import User from "~/models/user";
import contentMixin from "../../mixins/contextMixin";
import dropdownControlMixin from '~/components/dynamicFieldsTrees/mixins/dropdownControlMixin'
import { IObjectModelActionViewReference } from '~/interfaces/contentManagementInterfaces';

export default Vue.extend({
  mixins: [objectModelActionConfirmationMixin, objectModelActionPerformMixin, contentMixin, dropdownControlMixin],
  components: {
    ObjectModelActionErrorModal,
    ObjectModelActionCustomNotification,
  },
  props: {
    contentElement: {
      type: Object as () => ListViewContentElement,
      required: true
    },
    entry: Object as () => ObjectModelEntry,
    listView: Object as () => ListView | PublishedListView, // this is not the same as the selectedPublishedListView for elements in list views embedded in detail views
    user: {
      type: User
    },
    isUserDashboard: {
      type: Boolean,
      default: false
    }
  },
  data: function (): object {
    return {
      NotesTabContext,
    };
  },
  computed: {
    associatedObjectModelEntry() {
      if (!this.isPreview) return this.entry;
    },
    objectModel() {
      if (this.listView instanceof PublishedListView) {
        return this.listView.published_object_model;
      } else {
        return this.listView.object_model;
      }
    },
    actionReferencesArray(): IObjectModelActionViewReference[] {
      return this.contentElement.contentElementsArray;
    },
    orderedActions() {
      if (!this.listView || !this.associatedObjectModelEntry || this.entry instanceof ObjectModelFakeEntry) return this.contentElement.contentElementsArray;
      return this.contentElement.contentElementsArray.filter(actionElement => {
        const action = this.objectModel.findObjectModelAction(actionElement.action_client_id);
        return action.isAvailableForEntry(this.associatedObjectModelEntry, this.user, this.listView);
      });
    },
    hasOnlyOneAction() {
      return this.orderedActions.length === 1;
    },
    objectModelEntryEditFrom(): object {
      return {};
    },
    actionPerformContext(): object {
      // this.listView may be a PublishedListView if the user is viewing a top-level list view, or a ListView if the
      // user is viewing a detail view w/ embedded list view
      const id = this.listView.list_view_id || this.listView.id;
      return {type: "list_view", id};
    },
  },
  updated() {
    // @ts-ignore
    $(<any>`#actionsDropdown-${this.dropdownControlId}`).dropdown();
  },
  methods: {
    performSelectedAction(): void {
      if (this.entry instanceof ObjectModelFakeEntry) return;
      const objectModelAction = this.selectedObjectModelAction;
      const relevantEntry = this.associatedObjectModelEntry;
      this.performAction(objectModelAction, relevantEntry, this.listView);
    },
    tryPerformingAction(action, listView) {
      // @ts-ignore
      $(<any>`#actionsDropdown-${this.dropdownControlId}`).dropdown('hide');
      this.tryPerformAction(action, listView);
    }
  },
});
</script>